import React, { useState, useEffect, useMemo } from 'react';
import axios from '../axios-config';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, MessageSquare, FileText, Calculator, Upload, Send, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import DocumentUploader from '../components/DocumentUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

// Module 7 — Smart Quotation Forms per category
const SMART_FORMS = {
  'Housekeeping AMC': {
    fields: [
      { key: 'monthly_cost', label: 'Monthly Cost (₹)', type: 'number', required: true, isCost: true },
      { key: 'staff_count', label: 'Number of Staff', type: 'number', required: true },
      { key: 'supervisor_included', label: 'Supervisor Included?', type: 'boolean' },
      { key: 'materials_included', label: 'Materials/Consumables Included?', type: 'boolean' },
    ],
    annualMultiplier: 12,
  },
  'Security Services': {
    fields: [
      { key: 'monthly_cost', label: 'Monthly Cost (₹)', type: 'number', required: true, isCost: true },
      { key: 'guard_count', label: 'Number of Guards', type: 'number', required: true },
      { key: 'supervisor_included', label: 'Supervisor Included?', type: 'boolean' },
      { key: 'shift_hours', label: 'Shift Hours per Guard', type: 'number' },
    ],
    annualMultiplier: 12,
  },
  'Lift AMC': {
    fields: [
      { key: 'annual_amc_cost', label: 'Annual AMC Cost (₹)', type: 'number', required: true, isCost: true },
      { key: 'lift_count', label: 'Number of Lifts Covered', type: 'number', required: true },
      { key: 'breakdown_support', label: 'Breakdown Support Included?', type: 'boolean' },
      { key: 'spare_parts_included', label: 'Spare Parts Included?', type: 'boolean' },
    ],
    annualMultiplier: 1,
  },
  'CCTV AMC': {
    fields: [
      { key: 'annual_amc_cost', label: 'Annual AMC Cost (₹)', type: 'number', required: true, isCost: true },
      { key: 'camera_count', label: 'Number of Cameras', type: 'number', required: true },
      { key: 'cloud_storage_included', label: 'Cloud Storage Included?', type: 'boolean' },
    ],
    annualMultiplier: 1,
  },
  'Pest Control Services': {
    fields: [
      { key: 'cost_per_visit', label: 'Cost per Visit (₹)', type: 'number', required: true },
      { key: 'visits_per_year', label: 'Visits per Year', type: 'number', required: true },
      { key: 'annual_contract_cost', label: 'Total Annual Cost (₹)', type: 'number', required: true, isCost: true },
    ],
    annualMultiplier: 1,
  },
  'Fire Safety Systems': {
    fields: [
      { key: 'annual_amc_cost', label: 'Annual AMC Cost (₹)', type: 'number', required: true, isCost: true },
      { key: 'fire_extinguishers_covered', label: 'Fire Extinguishers Covered', type: 'number' },
      { key: 'refilling_included', label: 'Refilling Included?', type: 'boolean' },
    ],
    annualMultiplier: 1,
  },
  'Generator AMC': {
    fields: [
      { key: 'annual_amc_cost', label: 'Annual AMC Cost (₹)', type: 'number', required: true, isCost: true },
      { key: 'generator_kva', label: 'Generator Capacity (KVA)', type: 'number' },
      { key: 'oil_filter_included', label: 'Oil & Filter Changes Included?', type: 'boolean' },
    ],
    annualMultiplier: 1,
  },
  'Plumbing Works': {
    fields: [
      { key: 'total_cost', label: 'Total Quoted Cost (₹)', type: 'number', required: true, isCost: true },
      { key: 'completion_days', label: 'Completion Time (days)', type: 'number' },
      { key: 'materials_included', label: 'Materials Included?', type: 'boolean' },
    ],
    annualMultiplier: 1,
  },
  'Electrical Works': {
    fields: [
      { key: 'total_cost', label: 'Total Quoted Cost (₹)', type: 'number', required: true, isCost: true },
      { key: 'completion_days', label: 'Completion Time (days)', type: 'number' },
      { key: 'licensed_electrician', label: 'Work Done by Licensed Electrician?', type: 'boolean' },
    ],
    annualMultiplier: 1,
  },
};

const DEFAULT_FORM = {
  fields: [
    { key: 'total_cost', label: 'Total Quoted Cost (₹)', type: 'number', required: true, isCost: true },
    { key: 'completion_days', label: 'Completion Time (days)', type: 'number' },
    { key: 'warranty_months', label: 'Warranty Period (months)', type: 'number' },
  ],
  annualMultiplier: 1,
};

const STEPS = [
  { id: 1, label: 'Review Tender', icon: FileText },
  { id: 2, label: 'Ask Clarifications', icon: MessageSquare },
  { id: 3, label: 'Enter Commercials', icon: Calculator },
  { id: 4, label: 'Upload Documents', icon: Upload },
  { id: 5, label: 'Submit Bid', icon: Send },
];

export default function GuidedBidWizard() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [tender, setTender] = useState(null);
  const [clarifications, setClarifications] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [askingClr, setAskingClr] = useState(false);
  const [smartData, setSmartData] = useState({});
  const [comments, setComments] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const schema = useMemo(() => SMART_FORMS[tender?.work_category] || DEFAULT_FORM, [tender]);
  const costField = schema.fields.find(f => f.isCost);
  const totalQuoted = useMemo(() => {
    const base = Number(smartData[costField?.key] || 0);
    return base * (schema.annualMultiplier || 1);
  }, [smartData, schema, costField]);

  const load = async () => {
    try {
      const [tRes, clrRes] = await Promise.all([
        axios.get(`${API}/tenders/${tenderId}`),
        axios.get(`${API}/tenders/${tenderId}/clarifications`).catch(() => ({ data: [] })),
      ]);
      setTender(tRes.data);
      setClarifications(Array.isArray(clrRes.data) ? clrRes.data : []);
    } catch (e) {
      toast.error('Failed to load tender');
      navigate('/supplier-dashboard');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tenderId]);

  const askClarification = async () => {
    if (!newQuestion.trim()) { toast.error('Type your question first'); return; }
    setAskingClr(true);
    try {
      await axios.post(`${API}/supplier/clarifications`, { tender_id: tenderId, question: newQuestion.trim() });
      toast.success('Clarification submitted');
      setNewQuestion('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAskingClr(false); }
  };

  const canProceed = () => {
    if (step === 3) {
      // require all required smart-form fields
      return schema.fields.filter(f => f.required).every(f => {
        const v = smartData[f.key];
        return v !== undefined && v !== '' && v !== null;
      });
    }
    return true;
  };

  const submitBid = async () => {
    if (!totalQuoted || totalQuoted <= 0) { toast.error('Please enter a valid quote amount'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bids`, {
        tender_id: tenderId,
        quoted_amount: totalQuoted,
        comments,
        attachments,
        smart_form_data: { category: tender.work_category, schema: schema.fields.map(f => f.key), values: smartData, annual_multiplier: schema.annualMultiplier },
      });
      toast.success('Bid submitted successfully');
      navigate('/supplier-dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!tender) return null;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-navy text-white sticky top-0 z-30">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/supplier-dashboard')} className="text-white/80 hover:text-white inline-flex items-center text-sm" data-testid="wizard-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Exit
          </button>
          <div className="font-bold text-sm">Guided Bid · Step {step}/5</div>
          <div className="w-12" />
        </nav>

        {/* Step progress bar */}
        <div className="bg-navy-light/40 px-2 pb-3 pt-1">
          <div className="container mx-auto flex justify-between items-center gap-1 max-w-2xl">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const done = step > s.id;
              const active = step === s.id;
              return (
                <div key={s.id} className="flex-1 flex flex-col items-center" data-testid={`wizard-step-indicator-${s.id}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${done ? 'bg-gold border-gold text-navy' : active ? 'bg-orange border-orange text-white' : 'bg-transparent border-white/30 text-white/50'}`}>
                    {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <div className={`text-[9px] mt-1 leading-tight text-center ${active ? 'text-gold font-bold' : 'text-white/60'}`}>
                    {s.label.split(' ').map((w, i) => <div key={i}>{w}</div>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-5 max-w-2xl">
        {step === 1 && (
          <Step title="Step 1 · Review Tender" subtitle="Read the requirement carefully before quoting" testid="wizard-step-1">
            <div className="bg-card border border-border rounded-lg p-4 space-y-2">
              <div className="text-base font-bold text-navy" data-testid="wizard-tender-title">{tender.title}</div>
              <div className="text-xs text-muted-foreground">Category: <strong className="text-navy">{tender.work_category || 'General'}</strong></div>
              <div className="text-xs text-muted-foreground">Estimated Value: <strong className="text-navy">{fmt(tender.estimated_value)}</strong></div>
              <div className="text-xs text-muted-foreground">Submission deadline: <strong className="text-navy">{tender.submission_deadline ? new Date(tender.submission_deadline).toLocaleString() : '—'}</strong></div>
              {tender.scope_of_work && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wide mb-1">Scope of Work</div>
                  <div className="text-xs whitespace-pre-wrap leading-relaxed text-foreground/90 bg-muted/40 rounded p-3 max-h-72 overflow-y-auto" data-testid="wizard-scope">{tender.scope_of_work}</div>
                </div>
              )}
            </div>
          </Step>
        )}

        {step === 2 && (
          <Step title="Step 2 · Ask Clarifications" subtitle="Need anything clarified before quoting? Ask the buyer here." testid="wizard-step-2">
            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              <textarea value={newQuestion} onChange={e => setNewQuestion(e.target.value)} rows={3} placeholder="e.g., Does the scope include weekly deep cleaning?" className="w-full px-3 py-2 rounded border border-input bg-background text-sm" data-testid="wizard-clr-input" />
              <button onClick={askClarification} disabled={askingClr || !newQuestion.trim()} className="w-full px-4 py-2.5 rounded bg-orange text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center" data-testid="wizard-clr-submit-btn">
                <MessageSquare className="h-4 w-4 mr-2" /> {askingClr ? 'Sending…' : 'Submit Question'}
              </button>
            </div>
            <div className="mt-3 space-y-2" data-testid="wizard-clr-list">
              {clarifications.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No clarifications yet on this tender.</p>
              ) : clarifications.map(c => (
                <div key={c.id} className="bg-card border border-border rounded p-3 text-xs">
                  <div className="font-semibold text-navy">Q: {c.question}</div>
                  {c.answer ? (
                    <div className="mt-1.5 text-foreground/80"><strong className="text-green-700">A:</strong> {c.answer}</div>
                  ) : (
                    <div className="mt-1.5 text-orange italic">Awaiting buyer response…</div>
                  )}
                </div>
              ))}
            </div>
          </Step>
        )}

        {step === 3 && (
          <Step title="Step 3 · Enter Commercial Details" subtitle="No Excel needed — fill the smart form below" testid="wizard-step-3">
            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              <div className="text-[11px] text-muted-foreground bg-muted/40 rounded p-2 flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-orange flex-shrink-0" />
                <span>Smart form for <strong>{tender.work_category || 'this category'}</strong>. All required fields marked with *.</span>
              </div>
              {schema.fields.map(f => (
                <div key={f.key} data-testid={`smart-field-${f.key}`}>
                  <label className="block text-xs font-bold text-navy mb-1">{f.label}{f.required && ' *'}</label>
                  {f.type === 'boolean' ? (
                    <div className="flex gap-3">
                      <label className="inline-flex items-center text-sm">
                        <input type="radio" name={f.key} checked={smartData[f.key] === true} onChange={() => setSmartData({ ...smartData, [f.key]: true })} className="mr-1.5 w-4 h-4" data-testid={`smart-field-${f.key}-yes`} /> Yes
                      </label>
                      <label className="inline-flex items-center text-sm">
                        <input type="radio" name={f.key} checked={smartData[f.key] === false} onChange={() => setSmartData({ ...smartData, [f.key]: false })} className="mr-1.5 w-4 h-4" data-testid={`smart-field-${f.key}-no`} /> No
                      </label>
                    </div>
                  ) : (
                    <input type="number" min="0" value={smartData[f.key] ?? ''} onChange={e => setSmartData({ ...smartData, [f.key]: e.target.value === '' ? '' : Number(e.target.value) })} className="w-full px-3 py-3 rounded border border-input bg-background text-base" data-testid={`smart-field-${f.key}-input`} />
                  )}
                </div>
              ))}
              <div className="bg-navy text-white rounded-lg p-4 mt-2" data-testid="wizard-total-summary">
                <div className="text-[10px] uppercase font-bold text-gold tracking-wide">Auto-Computed Total Quote</div>
                <div className="text-2xl font-bold mt-1" data-testid="wizard-total-amount">{fmt(totalQuoted)}</div>
                <div className="text-[11px] text-white/70 mt-0.5">{schema.annualMultiplier > 1 ? `${costField?.label} × ${schema.annualMultiplier} months` : 'Single-cycle total'}</div>
              </div>
              <div>
                <label className="block text-xs font-bold text-navy mb-1">Additional Comments (optional)</label>
                <textarea value={comments} onChange={e => setComments(e.target.value)} rows={2} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" placeholder="Anything else the buyer should know?" data-testid="wizard-comments" />
              </div>
            </div>
          </Step>
        )}

        {step === 4 && (
          <Step title="Step 4 · Upload Supporting Documents" subtitle="Brochure, sample work, certifications, etc. (optional)" testid="wizard-step-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <DocumentUploader purpose="bid_attachments" maxFiles={5} files={attachments} onChange={setAttachments} />
              <div className="text-[11px] text-muted-foreground mt-2">Max 5 files. PDF / image preferred. Optional — your bid can be submitted without attachments.</div>
            </div>
          </Step>
        )}

        {step === 5 && (
          <Step title="Step 5 · Review & Submit" subtitle="Check your quote before submitting" testid="wizard-step-5">
            <div className="bg-card border-2 border-orange/40 rounded-lg p-4 space-y-3">
              <Row label="Tender" value={tender.title} />
              <Row label="Category" value={tender.work_category || 'General'} />
              <Row label="Quoted Amount" value={fmt(totalQuoted)} highlight />
              {Object.entries(smartData).map(([k, v]) => {
                const f = schema.fields.find(f => f.key === k);
                if (!f) return null;
                const display = typeof v === 'boolean' ? (v ? 'Yes' : 'No') : f.isCost ? fmt(v) : v;
                return <Row key={k} label={f.label.replace(' (₹)', '')} value={display} />;
              })}
              {comments && <Row label="Comments" value={comments} multiline />}
              <Row label="Attachments" value={`${attachments.length} file${attachments.length !== 1 ? 's' : ''}`} />
              <button onClick={submitBid} disabled={submitting || !totalQuoted} className="w-full px-4 py-3 rounded bg-orange text-white text-base font-bold disabled:opacity-50 inline-flex items-center justify-center mt-3" data-testid="wizard-submit-bid-btn">
                <Send className="h-4 w-4 mr-2" /> {submitting ? 'Submitting…' : `Submit Bid · ${fmt(totalQuoted)}`}
              </button>
            </div>
          </Step>
        )}
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-3 z-[10000] pb-[max(env(safe-area-inset-bottom),0.75rem)]">
        <div className="container mx-auto max-w-2xl flex items-center justify-between gap-3">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="px-4 py-2.5 rounded border border-border text-sm font-bold disabled:opacity-30 inline-flex items-center" data-testid="wizard-prev-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </button>
          {step < 5 ? (
            <button onClick={() => { if (canProceed()) setStep(step + 1); else toast.error('Please complete required fields'); }} className="flex-1 px-4 py-2.5 rounded bg-navy text-white text-sm font-bold inline-flex items-center justify-center" data-testid="wizard-next-btn">
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </button>
          ) : (
            <div className="text-xs text-muted-foreground text-center flex-1">Click Submit Bid above to finish</div>
          )}
        </div>
      </div>
    </div>
  );
}

const Step = ({ title, subtitle, testid, children }) => (
  <div data-testid={testid}>
    <div className="mb-4">
      <h2 className="text-lg font-bold text-navy">{title}</h2>
      <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
    </div>
    {children}
  </div>
);

const Row = ({ label, value, highlight, multiline }) => (
  <div className={`flex ${multiline ? 'flex-col' : 'justify-between items-center'} ${highlight ? 'bg-navy text-white rounded p-2 -mx-2' : ''}`}>
    <span className={`text-[10px] uppercase font-bold tracking-wide ${highlight ? 'text-gold' : 'text-muted-foreground'}`}>{label}</span>
    <span className={`${highlight ? 'text-xl text-white font-bold' : 'text-sm text-navy font-semibold'} ${multiline ? 'mt-1' : ''}`}>{value}</span>
  </div>
);
